import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/Buttons';
import speedyFlowSlice from '@/components/SpeedyFlow/redux/slice';
import { setQueryParams } from '@/hooks/route';
import { getProductsQueryOptions } from '@/queries/products';
import { ProductInterval } from '@/queries/products/types';

import ProductEligibilityResult from './ProductEligibilityResult';
import { ProductFeaturesList, ProductFeaturesListItem } from './ProductFeatureList';
import ProductIntervalSelector from './ProductIntervalSelector';
import ProductPrice from './ProductPrice';
import useCheckoutOrUpgrade from './useCheckoutOrUpgrade';
import { parseBenefits } from './utils';

interface EducatorSubscriptionProps {
  entryPoint?: string; // for analytics only
}

const { closeSpeedyFlow } = speedyFlowSlice.actions;

const EducatorSubscription = ({ entryPoint }: EducatorSubscriptionProps) => {
  const [productInterval, setProductInterval] = useState<ProductInterval>('month');
  const dispatch = useDispatch();

  const { data: products } = useQuery({
    ...getProductsQueryOptions({
      lookupKey: 'monthly-educator,annual-educator',
    }),
    select: (data) => data.list,
  });

  const monthlyProduct = products?.find((product) => product.productLookupKey === 'monthly-educator');
  const yearlyProduct = products?.find((product) => product.productLookupKey === 'annual-educator');

  const product = productInterval === 'year' ? yearlyProduct : monthlyProduct;

  const benefits = useMemo(() => parseBenefits(product), [product]);

  const { isUpgrade, label, disabled, loading, onClick } = useCheckoutOrUpgrade(product, {
    entryPoint,
    successPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
    cancelPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
    onUpgradeSuccess: () => {
      setQueryParams({ ppt: undefined });
      dispatch(closeSpeedyFlow());
    },
  });

  return (
    <div
      className="flex w-full max-w-[336px] flex-col items-center justify-center rounded-2xl bg-beige-light shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]"
      data-component="educator-pass"
    >
      <div className="mt-3 flex w-full flex-col items-center gap-4">
        <div className="w-full px-4">
          <h4 className="preflight preflight-h4 text-2xl/7 font-bold text-green-900">Educator Membership</h4>
          <p className="preflight preflight-p text-sm text-stone-500">Unlimited learning on top of existing access.</p>
        </div>
        <div className="flex w-full items-center justify-between gap-2 px-4">
          <ProductPrice currency={product?.price.currency} price={product?.price.price} />
          <ProductIntervalSelector value={productInterval} onChange={setProductInterval} className="bg-white" />
        </div>
        <ProductFeaturesList className="w-full px-4">
          {benefits.map((benefit, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ProductFeaturesListItem key={index.toString()}>{benefit}</ProductFeaturesListItem>
          ))}
        </ProductFeaturesList>
      </div>
      <div className="w-full p-4">
        <div className="px-4">
          <Button
            data-component="checkout-button"
            disabled={disabled}
            loading={loading}
            size="lg"
            className="w-full"
            onClick={onClick}
          >
            {label}
          </Button>
          {product && !isUpgrade && <ProductEligibilityResult result={product.eligibility} className="mt-3" />}
        </div>

        <p className="preflight preflight-p mt-2 text-center text-sm text-stone-500">Cancel Anytime</p>
      </div>
    </div>
  );
};

export default EducatorSubscription;
